import {
  Authorized,
  Connection, CreateStakeAccountWithSeedParams,
  LAMPORTS_PER_SOL, Lockup,
  PublicKey,
  RpcResponseAndContext, SignatureResult,
  StakeProgram,
} from '@solana/web3.js';
import { StakingPlan } from '../components/staking/surfaces/ActivateStake';
import { signAndSendTransaction } from '../utils/tokens';
import { formatLamports } from '../utils/utils';
import { Wallet } from '../utils/wallet';

export type StakingMode = 'classic' | 'new' | 'miner'

export default class StakingService {
  private readonly wallet: Wallet;
  private readonly connection: Connection;
  private readonly stakingBackendUrl: string;
  private readonly mode: StakingMode;

  constructor(wallet: Wallet, connection: Connection, stakingBackendUrl: string,
              mode: StakingMode) {
    this.wallet = wallet
    this.connection = connection
    this.stakingBackendUrl = stakingBackendUrl
    this.mode = mode
  }

  async getStakeAccount(): Promise<PublicKey> {
    const seed = this.getStakeAccountSeed();
    return this.wallet.getStakeAccount(seed);
  }

  async getAINodeStakeAccount(): Promise<PublicKey> {
    const seed = 'miner_stake';
    return this.wallet.getStakeAccount(seed);
  }

  getStakeAccountSeed(): string {
    switch (this.mode) {
      case 'classic':
        return 'stake';
      case 'new':
        return 'stake_new'
      case 'miner':
        return 'miner_stake';
      default:
        throw new Error(`Unknown staking mode: ${this.mode}`);
    }
  }

  async checkAllowedDelegator(): Promise<boolean> {
    try {
      const response = await fetch(
        `${this.stakingBackendUrl}/v2/allowed?domichainAddress=${this.wallet.publicKey.toBase58()}`,
      );
      
      if (!response.ok) {
        return false;
      }
      
      const { data } = await response.json();
      return data.isAllowed;
    } catch (error) {
      console.error('Failed to check allowed delegator status:', error);
      return false;
    }
  }

  async delegateStake(stakeAccount: PublicKey, tokenAmount: string,
                      stakingLimit: number, votePubkey: PublicKey,
                      lockupPeriodSeconds?: number | undefined
  ): Promise<string> {
    const stakeMinimumDelegation = await this.connection.getStakeMinimumDelegation();
    const lamports = Math.round(parseFloat(tokenAmount) * LAMPORTS_PER_SOL);

    if (!lamports || lamports < stakeMinimumDelegation.value) {
      throw new Error(
        `Stake amount should be more than ${formatLamports(
          stakeMinimumDelegation.value,
        )} DOMI`,
      );
    }

    if (stakingLimit > 0 && lamports > stakingLimit * LAMPORTS_PER_SOL) {
      throw new Error(`Stake amount should not exceed ${stakingLimit} DOMI`);
    }

    {
      const seed = this.getStakeAccountSeed();

      const result = await this._createStakingAccount(stakeAccount, seed,
        lamports, lockupPeriodSeconds);

      if (result.value.err) {
        throw new Error(
          `Failed to confirm create account transaction: ${result.value.err}`,
        );
      }
    }

    return await this._delegateStake(stakeAccount, votePubkey);
  }

  async deactivateStake(stakeAccount: PublicKey): Promise<string> {
    const transaction = StakeProgram.deactivate({
      stakePubkey: stakeAccount,
      authorizedPubkey: this.wallet.publicKey,
    });

    const { blockhash, lastValidBlockHeight } =
      await this.connection.getLatestBlockhash();

    transaction.feePayer = this.wallet.publicKey;
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;

    return await signAndSendTransaction(this.connection, transaction, this.wallet, []);
  }

  async withdrawStake(stakeAccount: PublicKey): Promise<string> {
    const transaction = StakeProgram.withdraw({
      stakePubkey: stakeAccount,
      authorizedPubkey: this.wallet.publicKey,
      toPubkey: this.wallet.publicKey,
      lamports: await this.connection.getBalance(stakeAccount),
    });

    const { blockhash, lastValidBlockHeight } =
      await this.connection.getLatestBlockhash();

    transaction.feePayer = this.wallet.publicKey;
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;

    return await signAndSendTransaction(this.connection, transaction, this.wallet, []);
  };

  async registerStake(stakeAccount: PublicKey): Promise<any | null> {
    const stakeAccountInfo = await this.connection.getStakeActivation(
      stakeAccount,
    );
    const stakeAmount = stakeAccountInfo.active + stakeAccountInfo.inactive;

    if (this.mode == 'classic') {
      return { stakeAmount };
    }

    try {
      const message = Buffer.from('domichain-staking');
      const signature = await this.wallet.createSignature(message);

      const response = await fetch(
        `${this.stakingBackendUrl}/v2/stakes/${stakeAccount.toBase58()}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ownerAddress: this.wallet.publicKey.toBase58(),
            stakeAmount,
            signature,
          }),
        },
      );
      const { data } = await response.json()
      return data;
    } catch (error) {
      console.error('Failed to save initial stake in the backend', error);
    }
    return null;
  }

  async deregisterStake(stakeAccount: PublicKey): Promise<any | null> {
    try {
      if (this.mode == 'classic') {
        return null;
      }

      const message = Buffer.from('domichain-staking');
      const signature = await this.wallet.createSignature(message);

      const searchParams = new URLSearchParams();
      searchParams.append('ownerAddress', this.wallet.publicKey.toBase58());
      searchParams.append('signature', signature);

      const response = await fetch(`${this.stakingBackendUrl}/v2/stakes/${stakeAccount.toBase58()}`, {
        method: 'DELETE',
        body: searchParams,
      });
      const { data } = await response.json()
      return data;
    } catch (error) {
      console.warn(`Failed to delete active stake info: `, error);
    }
    return null;
  }

  async getStakingRewards(stakeAccount: PublicKey): Promise<any | null> {
    if (this.mode == 'classic') {
      return null;
    }

    if (this.mode === 'miner') {
      return await this.getAINodeRewards(stakeAccount);
    }
  
    try {
      // Try new rewards first
      const response = await fetch(
        `${this.stakingBackendUrl}/v2/stakes/rewards/${stakeAccount.toBase58()}`,
      );
  
      if (!response.ok) {
        // If new rewards fail, try deprecated rewards
        const depResponse = await fetch(
          `${this.stakingBackendUrl}/v2/stakes/dep-rewards/${stakeAccount.toBase58()}`,
        );
        
        if (!depResponse.ok) {
          console.warn('Both new and deprecated reward endpoints failed');
          return null;
        }
  
        const { data } = await depResponse.json();
        return data;
      }
  
      const { data } = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to fetch staking rewards:', error);
      return null;
    }
  }

  async claimRewards(stakeAccount: PublicKey): Promise<any | null> {
    if (this.mode == 'classic') {
      return null;
    }
  
    const message = Buffer.from('domichain-staking');
    const signature = await this.wallet.createSignature(message);

    if (this.mode === 'miner') {
      return await this.claimAINodeRewards(stakeAccount, signature);
    }

    const body = JSON.stringify({
      ownerAddress: this.wallet.publicKey.toBase58(),
      signature,
    });
  
    try {
      // Try new rewards first
      const response = await fetch(
        `${this.stakingBackendUrl}/v2/stakes/rewards/${stakeAccount.toBase58()}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body,
        },
      );
  
      if (!response.ok) {
        // If new rewards fail, try deprecated rewards
        const depResponse = await fetch(
          `${this.stakingBackendUrl}/v2/stakes/dep-rewards/${stakeAccount.toBase58()}`,
          {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body,
          },
        );
  
        if (!depResponse.ok) {
          console.warn('Both new and deprecated reward endpoints failed');
          return null;
        }
  
        const { data } = await depResponse.json();
        return data;
      }
  
      const { data } = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to claim rewards:', error);
      return null;
    }
  }

  // AI Node Specific functions
  async fetchAINodeStakePlan(): Promise<StakingPlan> {
    try {
      const response = await fetch(`${this.stakingBackendUrl}/v2/miner/getplans`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch AI Node stake plan: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Extract the AI Node specific plan
      const aiNodeTiers = data.data.stakingTiers.filter(tier => 
        tier.tierName.startsWith('M')
      );
      
      if (aiNodeTiers.length === 0) {
        throw new Error('No AI Node staking tiers found');
      }
      
      // Find the tier that matches the user's certificate type
      const certificate = await this.getAINodeCertificate();
      const minerType = certificate?.miner_type || certificate?.type || 'M1';
      const matchingTier = aiNodeTiers.find(tier => tier.tierName === minerType) || aiNodeTiers[0];
      
      return {
        id: matchingTier.id,
        planName: `AI Node ${matchingTier.tierName} Staking`,
        apy: matchingTier.dailyReward * 365 / matchingTier.minStakeAmount * 100,
        planDescription: `Earn rewards with your ${matchingTier.tierName} AI Node`,
        minStakeAmount: matchingTier.minStakeAmount,
        validatorVotePubkey: data.data.validatorVotePubkey,
        lockupEnabled: true,
        lockupPeriodSeconds: matchingTier.periodDays * 86400,
        minerType: minerType,
      };
    } catch (error) {
      console.error('Error fetching AI Node stake plan:', error);
      throw error;
    }
  }

  async checkAINodeStatus(): Promise<{ hasAINode: boolean, nodeType?: string }> {
    const certificate = await this.getAINodeCertificate();
    if (!certificate) {
      return { hasAINode: false };
    }
    
    return { 
      hasAINode: true, 
      nodeType: certificate.miner_type || certificate.minerType || 'Standard' 
    };
  }

  async getAINodeCertificate(): Promise<any | null> {
    if (!this.wallet.publicKey) {
      throw new Error('Wallet not connected');
    }
  
    try {
      // Get the AI Node stake account address
      const aiNodeStakeAccount = await this.getAINodeStakeAccount();
      
      // Use the stake account to fetch information
      const response = await fetch(
        `${this.stakingBackendUrl}/v2/miner/stakeaccount?stakeAccountAddress=${aiNodeStakeAccount.toBase58()}`
      );
  
      if (!response.ok) {
        return null;
      }
  
      const { data } = await response.json();
      
      if (data.minerStake) {
        // console.log(data.minerStake.tierName);
        return {
          type: data.minerStake.tierName,
          miner_type: data.minerStake.tierName
        };
      } else if (data.lockedStake) {
        // console.log(data.lockedStake.tierName);
        return {
          type: data.lockedStake.tierName,
          miner_type: data.lockedStake.tierName
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting AI Node information:', error);
      return null;
    }
  }

  async getAINodeRewards(stakeAccount: PublicKey): Promise<any | null> {
    try {
      // Get stakeaccount data
      const response = await fetch(
        `${this.stakingBackendUrl}/v2/miner/stakeaccount?stakeAccountAddress=${stakeAccount.toBase58()}`
      );
      
      if (response.ok) {
        const { data } = await response.json();
        // console.log('stakeaccount response:', data);
        
        // Return the minerStake data if available (M1 node)
        if (data.minerStake) {
          return data.minerStake;
        } 
        // For M2/M3 nodes with locked stake
        else if (data.lockedStake) {
          const lockedStake = data.lockedStake;
          
          // Fetch the stake plans to get tier information
          const plansResponse = await fetch(`${this.stakingBackendUrl}/v2/miner/getplans`);
          if (!plansResponse.ok) {
            throw new Error('Failed to fetch staking plans');
          }
          
          const plansData = await plansResponse.json();
          // console.log('plans response:', plansData);
          
          // Find matching tier for current node
          const tierName = lockedStake.tierName;
          const matchingTier = plansData.data.stakingTiers.find(
            tier => tier.tierName === tierName
          );
          
          if (!matchingTier) {
            console.error('No matching tier found for', tierName);
            return lockedStake;
          }
          
          // Calculate rewards using same logic as backend
          const SECONDS_IN_A_DAY = 86400;
          const lockStartTime = new Date(lockedStake.lockStartedAt).getTime();
          const currentTime = Date.now();
          
          const daysPassed = Math.min(
            Math.floor((currentTime - lockStartTime) / (1000 * SECONDS_IN_A_DAY)),
            matchingTier.periodDays
          );
          
          // Calculate unlocked rewards (same formula as backend)
          const dailyReward = matchingTier.dailyReward;
          const totalExpectedReward = matchingTier.totalReward;
          const unlockedRewards = Math.min(daysPassed * dailyReward, totalExpectedReward);
          
          // Get extra reward percentage for locked stakes
          const extraRewardPercentage = lockedStake.extraRewardPercentage || 0;
          
          // Apply extra reward percentage if applicable
          const extraRewardAmount = Math.floor(unlockedRewards * (extraRewardPercentage / 100));
          const totalUnlockedForWithdraw = unlockedRewards + extraRewardAmount;
          
          // Calculate available for withdraw (unlocked minus already withdrawn)
          const withdrawnAmount = lockedStake.withdrawnAmount || 0;
          const availableForWithdraw = Math.max(totalUnlockedForWithdraw - withdrawnAmount, 0);
          
          return {
            // Original locked stake fields
            id: lockedStake.id,
            status: lockedStake.status,
            tierName: lockedStake.tierName,
            initialLockAmount: lockedStake.initialLockAmount,
            lockStartedAt: lockedStake.lockStartedAt,
            lockPeriodSeconds: lockedStake.lockPeriodSeconds,
            lockEndDate: lockedStake.lockEndDate,
            extraRewardPercentage,
            
            // Mapped fields for compatibility
            initialStakeAmount: lockedStake.initialLockAmount,
            stakeStartedAt: lockedStake.lockStartedAt,
            stakingPeriodSeconds: lockedStake.lockPeriodSeconds,
            
            // Calculated reward fields
            totalExpectedReward: totalExpectedReward,
            availableForWithdraw: availableForWithdraw,
            withdrawnAmount: withdrawnAmount,
            minAllowedWithdrawAmount: 1000000, // 0.001 DOMI minimum 
          };
        }
        
        return null;
      }
    } catch (error) {
      console.error('Failed to fetch AI Node rewards:', error);
      return null;
    }
  }
  
  async claimAINodeRewards(stakeAccount: PublicKey, signature?: string): Promise<any | null> {
    if (!signature) {
      const message = Buffer.from('domichain-staking');
      signature = await this.wallet.createSignature(message);
    }
    
    try {
      const stakeInfo = await this.getAINodeRewards(stakeAccount);
      
      if (!stakeInfo) {
        console.warn('No AI Node stake info found for withdrawal');
        return null;
      }
      
      if (stakeInfo.id) {
        const response = await fetch(
          `${this.stakingBackendUrl}/v2/miner/withdraw`,
          {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              stakeId: stakeInfo.id,
              ownerAddress: this.wallet.publicKey.toBase58(),
              signature,
            }),
          }
        );
        
        if (!response.ok) {
          console.warn('Failed to claim AI Node rewards:', response.statusText);
          return null;
        }
        
        const { data } = await response.json();
        return data;
      }
      
      console.warn('Unable to determine stake ID for rewards claim');
      return null;
    } catch (error) {
      console.error('Failed to claim AI Node rewards:', error);
      return null;
    }
  }
  

  async fetchActiveStakeInfo(stakeAccount: PublicKey): Promise<any | null> {
    if (this.mode == 'classic') {
      return null;
    }

    const response = await fetch(
      `${this.stakingBackendUrl}/v2/stakes/${stakeAccount.toBase58()}`,
    );
    const { data } = await response.json();
    return data;
  }

  async fetchDepositAddress(stakingPlanId: number): Promise<string> {
    const searchParams = new URLSearchParams()
    searchParams.append('stakingPlanId', stakingPlanId.toString());
    searchParams.append('domichainAddress', this.wallet.publicKey?.toBase58());

    const response = await fetch(`${this.stakingBackendUrl}/v1/deposit`, {
      body: searchParams,
    });
    if (!response.ok) {
      throw new Error('No deposit address');
    }

    const { data } = await response.json();
    return data.depositAddress;
  }

  async fetchAssignedValidatorInfo(): Promise<any> {
    const searchParams = new URLSearchParams()
    searchParams.append('domichainAddress', this.wallet.publicKey?.toBase58());

    const response = await fetch(`${this.stakingBackendUrl}/v2/validator`, {
      body: searchParams,
    });
    if (!response.ok) {
      throw new Error('No assigned validator yet');
    }

    const { data } = await response.json();
    return data;
  }

  async fetchStakingPlans(): Promise<StakingPlan[]> {
    const response = await fetch(`${this.stakingBackendUrl}/v2/plans`);
    const data = await response.json();
    return data.data.plans as StakingPlan[];
  }

  async fetchNewStakePlan(): Promise<StakingPlan> {
    const response = await fetch(`${this.stakingBackendUrl}/v2/new-stake/plan`);
    const data = await response.json();
    return data.data as StakingPlan;
  }

  async _createStakingAccount(stakeAccount: PublicKey, stakeAccountSeed: string,
                              lamports: number,
                              lockupPeriodSeconds?: number | undefined
  ): Promise<RpcResponseAndContext<SignatureResult>> {
    const createAccountParams: CreateStakeAccountWithSeedParams = {
      fromPubkey: this.wallet.publicKey,
      basePubkey: this.wallet.publicKey,
      stakePubkey: stakeAccount,
      seed: stakeAccountSeed,
      authorized: new Authorized(this.wallet.publicKey, this.wallet.publicKey),
      lamports: lamports,
    }
    if (lockupPeriodSeconds) {
      const lockupTimestamp = Math.floor(Date.now()/1000) + lockupPeriodSeconds;
      createAccountParams.lockup = new Lockup(lockupTimestamp, 0, PublicKey.default)
    }

    const { blockhash, lastValidBlockHeight } =
      await this.connection.getLatestBlockhash();

    const transaction = StakeProgram.createAccountWithSeed(createAccountParams);
    transaction.feePayer = this.wallet.publicKey;
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;

    const signature = await signAndSendTransaction(
      this.connection,
      transaction,
      this.wallet,
      [],
    );

    return await this.connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      'finalized',
    );
  }

  async _delegateStake(stakeAccount: PublicKey, votePubkey: PublicKey): Promise<string> {
    const transaction = StakeProgram.delegate({
      stakePubkey: stakeAccount,
      authorizedPubkey: this.wallet.publicKey,
      votePubkey: votePubkey,
    });

    const { blockhash, lastValidBlockHeight } =
      await this.connection.getLatestBlockhash();

    transaction.feePayer = this.wallet.publicKey;
    transaction.recentBlockhash = blockhash;
    transaction.lastValidBlockHeight = lastValidBlockHeight;

    const signature = await signAndSendTransaction(this.connection, transaction, this.wallet, []);

    const result = await this.connection.confirmTransaction(
      { signature, blockhash, lastValidBlockHeight },
      'finalized',
    );

    if (result.value.err) {
      throw new Error(
        `Failed to confirm delegate stake transaction: ${result.value.err}`,
      );
    }
    return signature;
  }
}
